import {
  Button,
  HStack,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
} from "@chakra-ui/react";
import React from "react";
import {
  DateRange as DayPickerDateRange,
  DayPicker,
  TZDate,
} from "react-day-picker";
import { DateRangePickerPreset, DatePickerRange } from "./DateRangePicker";
import { DateRangePresetsDropdown } from "./DateRangePresetsDropdown";
import { DayPickerChevron } from "./DayPickerChevron";
import { DayPickerDropdown } from "./DayPickerDropdown";
import { getNextDateRangeSelection } from "./getNextDateRangeSelection";
import { toTZDate } from "./tzDateUtils";

type DateRangePickerSinglePopoverContentProps = {
  dateRange?: DatePickerRange;
  earliestDate?: Date;
  latestDate?: Date;
  presetTitle: string;
  presetDateRanges?: DateRangePickerPreset[];
  onSelectRange: (dateRange: DatePickerRange | undefined) => void;
  onClear: () => void;
  onClose: () => void;
  timeZone: string;
};

export const DateRangePickerSinglePopoverContent = React.forwardRef<
  HTMLDivElement,
  DateRangePickerSinglePopoverContentProps
>(
  (
    {
      dateRange,
      earliestDate,
      latestDate,
      presetTitle,
      presetDateRanges,
      onSelectRange,
      onClear,
      onClose,
      timeZone,
    },
    ref,
  ) => {
    const pickerEarliestDate =
      earliestDate ? toTZDate(earliestDate, timeZone) : undefined;

    const pickerLatestDate =
      latestDate ? toTZDate(latestDate, timeZone) : undefined;

    const disabledDays = [
      ...(pickerEarliestDate ? [{ before: pickerEarliestDate }] : []),
      ...(pickerLatestDate ? [{ after: pickerLatestDate }] : []),
    ];

    const handleSelect = (
      _range: DayPickerDateRange | undefined,
      selectedDay: Date, // actually a TZDate range
    ) => {
      const nextDateRange = getNextDateRangeSelection({
        selectedDay: selectedDay as TZDate,
        dateRange,
        timeZone,
      });
      onSelectRange(nextDateRange);
    };

    return (
      <PopoverContent
        zIndex="dropdown"
        ref={ref}
        w="auto"
        maxW="100vw"
        bg="white"
      >
        <PopoverBody>
          <DayPicker
            mode="range"
            animate={true}
            timeZone={timeZone}
            defaultMonth={
              dateRange ?
                toTZDate(dateRange.from, timeZone)
              : TZDate.tz(timeZone)
            }
            numberOfMonths={1}
            selected={
              dateRange ?
                {
                  from: toTZDate(dateRange.from, timeZone),
                  to: toTZDate(dateRange.to, timeZone),
                }
              : undefined
            }
            onSelect={handleSelect}
            disabled={disabledDays}
            captionLayout="dropdown"
            navLayout="after"
            startMonth={pickerEarliestDate}
            endMonth={pickerLatestDate}
            reverseYears
            fixedWeeks
            showOutsideDays
            components={{
              Chevron: DayPickerChevron,
              Dropdown: DayPickerDropdown,
            }}
          />
        </PopoverBody>

        <PopoverFooter>
          <HStack justify="space-between" spacing={3} flexWrap="wrap">
            {presetDateRanges && presetDateRanges.length > 0 && (
              <DateRangePresetsDropdown
                value={dateRange}
                title={presetTitle}
                presetDateRanges={presetDateRanges}
                onSelect={onSelectRange}
              />
            )}

            <HStack>
              {dateRange && (
                <Button
                  size="sm"
                  colorScheme="error"
                  variant="ghost"
                  onClick={onClear}
                >
                  Clear
                </Button>
              )}

              {!dateRange && (
                <Button
                  size="sm"
                  colorScheme="gray"
                  variant="outline"
                  onClick={onClose}
                >
                  Close
                </Button>
              )}

              {dateRange && (
                <Button
                  size="sm"
                  colorScheme="primary"
                  variant="solid"
                  onClick={onClose}
                >
                  Done
                </Button>
              )}
            </HStack>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    );
  },
);
